/** SUNTRONIX ONE — Enterprise OS Shell (compact left sidebar + slim top bar).
 *  Non-destructive: every route/permission is preserved; navigation is a new
 *  presentation of the existing route map (constants/osNav.js). */
import { EntityBrandLogo } from "@/components/brand/EntityBrandLogo";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronRight, ChevronDown, LogOut, Plus, PanelLeftClose, PanelLeftOpen, Sparkles, UserCog,
  Laptop2, LayoutGrid, Menu, Search, Bell, CheckSquare, Home as HomeIcon, Sun, Zap } from "lucide-react";
import OneCommand from "@/components/app/OneCommand";
import EstablishmentSwitcher from "@/components/app/EstablishmentSwitcher";
import { OneMomentsProvider } from "@/components/app/OneMoments";
import { applyPrefs } from "@/lib/experience";
import { SidekickProvider, SidekickTrigger } from "@/components/app/OneSidekick";
import ApprovalsInboxChip, { APPROVER_ROLES } from "@/components/app/ApprovalsInboxChip";
import GlobalKycDrawer from "@/components/app/GlobalKycDrawer";
import UniversalCreate, { InstantCreate } from "@/components/app/UniversalCreate";
import { AdminModeProvider, AdminModeToggle } from "@/components/app/AdminMode";
import AskOneChat from "@/components/app/AskOneChat";
import StickyNotesWidget from "@/components/app/StickyNotesWidget";
import PreviewBanner, { PreviewDeviceFrame } from "@/components/app/PreviewBanner";
import SystemStatusChip from "@/components/os/SystemStatusChip";
import ModuleDirectory from "@/components/os/ModuleDirectory";
import { ModuleBar } from "@/components/os/ModuleBar";
import ContextHelp from "@/components/os/ContextHelp";
import { NotificationsPopover, StatusChip } from "@/layouts/SpacesShell";
import { canSee, canAdminUsers } from "@/constants/roleNav";
import { NAV, AUTH } from "@/constants/testIds";
import { OS_NAV, OS_NAV_BOTTOM, osGroupForPath } from "@/constants/osNav";
import { isCustomerRole } from "@/constants/customerNav";
import { WaHeaderShortcut, WA_HOME } from "@/components/whatsapp/WaHeaderShortcut";
import { MessageCircle, ShieldCheck } from "lucide-react";
import WhatsNewToast from "@/components/app/WhatsNewToast";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";


/** Breadcrumb-style page context: Workspace › Page (derived from the nav model, never guessed). */
function PageContext({ pathname, groups }) {
  const { search } = useLocation();
  const gid = osGroupForPath(pathname);
  const g = groups.find((x) => x.id === gid);
  if (!g) return null;
  const all = [...(g.children || []), ...(g.more || [])];
  const match = all.filter((c) => { const b = c.to.split("?")[0]; return pathname === b || (b !== "/" && pathname.startsWith(b + "/")); });
  const tab = new URLSearchParams(search).get("tab");
  const page = match.find((c) => tab && c.to.includes(`tab=${tab}`)) || match.find((c) => !c.to.includes("?")) || match[0];
  return (
    <nav aria-label="Breadcrumb" className="hidden 2xl:flex items-center gap-1.5 text-[12px] min-w-0 shrink-0 max-w-[320px]" data-testid="os-breadcrumb">
      <Link to={g.to} className="text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] truncate">{g.label}</Link>
      {page && page.to.split("?")[0] !== g.to && <><span className="text-[color:var(--st-text-muted)]/50">›</span><span className="font-medium text-[color:var(--st-text)] truncate">{page.label}</span></>}
    </nav>
  );
}

/** Secondary tools folded into one menu so the primary business action is never crowded. */
function MoreToolsMenu({ navigate, role }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="h-9 w-9 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)] hover:text-[color:var(--st-text)]" aria-label="More tools" title="More tools" data-testid="os-more-tools">
          <LayoutGrid className="h-[18px] w-[18px]" strokeWidth={1.7} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-2" data-testid="os-more-tools-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-[color:var(--st-text-muted)]">Assistants</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate("/copilot")} data-testid="os-tools-ai"><Sparkles className="h-4 w-4 mr-2" /> Suntronix AI Copilot</DropdownMenuItem>
        <div className="px-2 py-1"><SidekickTrigger /></div>
        <DropdownMenuSeparator />
        {APPROVER_ROLES.has(role) && <DropdownMenuItem className="sm:hidden" onClick={() => navigate("/approvals-inbox")} data-testid="os-tools-approvals"><ShieldCheck className="h-4 w-4 mr-2" /> Approvals inbox</DropdownMenuItem>}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-[color:var(--st-text-muted)]">Help & status</DropdownMenuLabel>
        <div className="px-2 py-1 flex items-center gap-2"><ContextHelp /><StatusChip /></div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-[color:var(--st-text-muted)]">Administration</DropdownMenuLabel>
        <div className="px-2 py-1"><AdminModeToggle /></div>
        <DropdownMenuItem onClick={() => navigate("/admin/control-centre")} data-testid="os-tools-settings"><UserCog className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function OsShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNav, setMobileNav] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  const [finVault, setFinVault] = useState(false);
  const [continuity, setContinuity] = useState(false);
  const [secCentre, setSecCentre] = useState(false);
  const [dirOpen, setDirOpen] = useState(false);

  useEffect(() => { applyPrefs(); }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/finance-vault/access")
      .then(({ data }) => setFinVault(!!(data.corporate || data.personal))).catch(() => setFinVault(false));
    api.get("/continuity/access")
      .then(({ data }) => setContinuity(!!(data.business || data.personal))).catch(() => setContinuity(false));
    api.get("/security-centre/access")
      .then(({ data }) => setSecCentre(!!data.allowed)).catch(() => setSecCentre(false));
  }, [user]);
  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  const visible = useCallback((item) => {
    if (item.financeVault && !finVault) return false;
    if (item.continuity && !continuity) return false;
    if (item.securityCentre && !secCentre) return false;
    if (item.userAdmin && !canAdminUsers(user?.role)) return false;
    if (item.onlyEmployee) return false;
    if (item.adminOnly && !["super_admin", "management"].includes(user?.role)) return false;
    return canSee(user?.role, item.to.split("?")[0]);
  }, [user, finVault, continuity, secCentre]);

  const groups = useMemo(() => {
    const filt = (list) => list.map((g) => {
      const children = (g.children || []).filter(visible);
      const advanced = (g.advanced || []).filter(visible);
      const more = (g.more || []).filter(visible);
      if (g.children && children.length === 0 && advanced.length === 0 && more.length === 0) return null;
      if (!g.children && !visible(g)) return null;
      return { ...g, children, more: [...advanced, ...more] };
    }).filter(Boolean);
    return { main: filt(OS_NAV), bottom: filt(OS_NAV_BOTTOM) };
  }, [visible]);

  const activeGroup = osGroupForPath(location.pathname);
  const [openGroup, setOpenGroup] = useState(activeGroup);
  useEffect(() => { setOpenGroup(activeGroup); }, [activeGroup]);
  // Same audience as the /admin/integrations/whatsapp route guard (SuntronixOnly) and the backend composer (is_suntronix): internal staff only.
  const internal = !!user && !isCustomerRole(user.role) && !String(user.role || "").startsWith("supplier");


  const handleLogout = useCallback(async () => {
    await logout(); toast.success("Signed out"); navigate("/login");
  }, [logout, navigate]);

  return (
    <AdminModeProvider user={user}>
    <SidekickProvider>
    <OneMomentsProvider enabled>
    <div className="h-[100dvh] overflow-hidden flex bg-[color:var(--st-bg)] os-shell" data-testid="os-shell">
      {/* ---------------- MAIN COLUMN ---------------- */}
      <div className="flex-1 min-w-0 flex flex-col">
        <PreviewBanner />
        <header className="h-14 shrink-0 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-2.5 md:px-4 lg:px-5 flex items-center gap-1 sm:gap-2 lg:gap-3" data-testid="os-topbar">
          <button className="md:hidden h-9 w-9 shrink-0 grid place-items-center rounded-md text-[color:var(--st-text-muted)]" onClick={() => setMobileNav(true)} aria-label="Open navigation" data-testid="os-mobile-menu">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center shrink-0 mr-1" data-testid="os-brand">
            <EntityBrandLogo />
          </Link>
          <span className="hidden md:inline-flex shrink-0"><EstablishmentSwitcher /></span>
          <PageContext pathname={location.pathname} groups={[...groups.main, ...groups.bottom]} />

          <div className="shrink-0 w-9 sm:w-auto sm:flex-1 sm:min-w-[8rem] mx-0.5 sm:mx-1 sm:max-w-xl">
            <OneCommand testId={NAV.searchInput} />
          </div>

          {/* one quote flow: the top-bar New Quote (InstantCreate) opens the full quotation form */}
          {/* <640 px: WhatsApp lives in the bottom bar, New Quote / Add Product in Create → Quotation / Product (same forms) */}
          {internal && <span className="hidden md:inline-flex shrink-0"><WaHeaderShortcut /></span>}
          <span className="hidden lg:inline-flex shrink-0"><InstantCreate role={user?.role} compact /></span>
          <CreateButton role={user?.role} />

          <div className="ml-auto flex items-center gap-0.5 shrink-0">
            <span className="hidden lg:inline-flex"><SystemStatusChip compact /></span>
            <span className="hidden sm:inline-flex"><ApprovalsInboxChip /></span>
            {/* <640 px: notifications are the bottom bar's Alerts tab */}
            <span className="hidden sm:inline-flex"><NotificationsPopover /></span>
            <MoreToolsMenu navigate={navigate} role={user?.role} />
            <ProfileMenu user={user} onLogout={handleLogout} />
          </div>
        </header>
        <ModuleBar groups={groups.main} settings={groups.bottom} activeGroup={activeGroup} onAllModules={() => setDirOpen(true)} />

        <div id="main-content" tabIndex={-1} role="main" data-scroll-container="true"
          className="flex-1 min-h-0 overflow-y-auto px-3 md:px-6 lg:px-8 py-4 pb-48 md:pb-32 os-main">
          <div className="mx-auto max-w-[1600px] os-fade-in" key={location.pathname}>
            <PreviewDeviceFrame><Outlet /></PreviewDeviceFrame>
          </div>
        </div>
      </div>

      <ModuleDirectory open={dirOpen} onOpenChange={setDirOpen} visible={visible} />
      <WhatsNewToast user={user} />
      <AskOneChat />
      <StickyNotesWidget />
      <GlobalKycDrawer open={kycOpen} onClose={() => setKycOpen(false)} user={user} />

      {/* ---------------- MOBILE NAV SHEET + BOTTOM BAR ---------------- */}
      <Sheet open={mobileNav} onOpenChange={setMobileNav}>
        <SheetContent side="left" className="w-[290px] p-0 bg-[color:var(--st-sidebar)] text-white border-r-0" data-testid="os-mobile-nav">
          <SheetTitle className="h-14 px-4 flex items-center border-b border-white/[0.08] font-display font-bold text-sm text-white">SUNTRONIX <span className="text-[color:var(--st-accent-bright)] ml-1">ONE</span></SheetTitle>
          <div className="px-3 py-2.5 border-b border-white/[0.08]" data-testid="os-mobile-company">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-1">Company</div>
            <div className="inline-flex rounded-lg bg-white"><EstablishmentSwitcher testid="establishment-switcher-mobile" optPrefix="establishment-mobile-opt" /></div>
          </div>
          <nav className="p-2 overflow-y-auto h-[calc(100%-56px-68px)] os-scroll">
            <button onClick={() => { setMobileNav(false); setDirOpen(true); }} data-testid="os-mobile-all-modules" className="os-nav-item w-full mb-1">
              <LayoutGrid className="h-[18px] w-[18px] os-nav-icon" strokeWidth={1.7} /><span className="text-[13px] font-medium whitespace-nowrap">All modules</span>
            </button>
            {[...groups.main, ...groups.bottom].map((g) => (
              <NavGroup key={g.id} group={g} active={activeGroup === g.id} collapsed={false} pathname={location.pathname} navigate={navigate} forceOpen />
            ))}
          </nav>
        </SheetContent>
      </Sheet>
      <MobileBar navigate={navigate} pathname={location.pathname} openMenu={() => setMobileNav(true)} showWa={internal} />
    </div>
    </OneMomentsProvider>
    </SidekickProvider>
    </AdminModeProvider>
  );
}

/* ------------------------------------------------------------------ */
function NavGroup({ group, active, open = false, onToggle, collapsed, pathname, navigate, forceOpen = false }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const Icon = group.icon;
  const more = useMemo(() => group.more || [], [group.more]);
  const hasChildren = (group.children && group.children.length > 0) || more.length > 0;
  useEffect(() => {
    if (active && more.some((c) => pathname === c.to.split("?")[0] || pathname.startsWith(c.to.split("?")[0] + "/"))) setMoreOpen(true);
  }, [active, pathname, more]);
  const isOpen = forceOpen || (open && !collapsed);
  const onHead = () => {
    if (!hasChildren || collapsed) { navigate(group.to); return; }
    if (!active) navigate(group.to);
    onToggle?.();
  };
  return (
    <div className="mb-0.5" data-testid={`os-nav-${group.id}`}>
      <button onClick={onHead} title={collapsed ? group.label : undefined} aria-expanded={hasChildren ? isOpen : undefined} aria-current={active ? "true" : undefined}
        data-testid={`os-nav-${group.id}-btn`}
        className={"os-nav-item w-full " + (active ? "os-nav-active" : "") + (collapsed ? " justify-center px-0" : "") + (group.accent === "ai" ? " os-nav-ai" : "")}>
        <Icon className="h-[18px] w-[18px] shrink-0 os-nav-icon" strokeWidth={1.7} />
        {!collapsed && <span className="flex-1 text-left text-[13px] font-medium truncate">{group.label}</span>}
        {!collapsed && hasChildren && (
          isOpen ? <ChevronDown className="h-3.5 w-3.5 opacity-50" /> : <ChevronRight className="h-3.5 w-3.5 opacity-50" />
        )}
      </button>
      {hasChildren && isOpen && (
        <ul className="ml-[22px] pl-3 border-l border-white/[0.08] my-1 space-y-px os-sub-enter">
          {group.children.map((c) => <SubLink key={c.to} c={c} pathname={pathname} />)}
          {more.length > 0 && (
            <li>
              <button onClick={() => setMoreOpen((m) => !m)} data-testid={`os-nav-${group.id}-more`} aria-expanded={moreOpen}
                className="w-full text-left px-2 py-[6px] rounded-md text-[11px] uppercase tracking-[0.12em] text-white/40 hover:text-white/80 flex items-center gap-1">
                {moreOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Advanced · {more.length}
              </button>
              {moreOpen && <ul className="space-y-px os-sub-enter">{more.map((c) => <SubLink key={c.to} c={c} pathname={pathname} hint />)}</ul>}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function SubLink({ c, pathname, hint }) {
  const { search } = useLocation();
  const [base, qs] = c.to.split("?");
  let cur = pathname === base || (base !== "/" && pathname.startsWith(base + "/"));
  if (cur && qs) {
    const want = new URLSearchParams(qs), have = new URLSearchParams(search);
    cur = [...want.entries()].every(([k, v]) => have.get(k) === v);
  } else if (cur && !qs && search) {
    cur = !new URLSearchParams(search).get("tab") && !new URLSearchParams(search).get("compose");
  }
  return (
    <li>
      <Link to={c.to} aria-current={cur ? "page" : undefined} data-testid={`os-subnav-${base.replace(/\//g, "-").replace(/^-/, "")}${qs ? "-" + qs.replace(/[^a-z0-9]+/gi, "-") : ""}`} title={hint && c.space ? `From ${c.space}` : undefined}
        className={"block px-2 py-[6px] rounded-md text-[12.5px] leading-tight transition-colors " + (cur ? "text-white bg-white/[0.08] font-medium" : "text-white/60 hover:text-white hover:bg-white/[0.05]")}>
        {c.label}
      </Link>
    </li>
  );
}

function CreateButton({ role }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "c" || e.key === "C") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      <button data-testid="one-bar-create" onClick={() => setOpen(true)} aria-expanded={open}
        className="h-9 px-3 rounded-full bg-[color:var(--st-text)] text-white text-[12px] font-semibold hover:bg-[#17263C] active:scale-[0.98] transition-[background-color,transform] flex items-center gap-1.5 shrink-0">
        <Plus className="h-4 w-4" strokeWidth={2} /><span className="hidden lg:inline">Create</span>
      </button>
      <UniversalCreate open={open} onOpenChange={setOpen} role={role} isCustomer={false} />
    </>
  );
}

function MobileBar({ navigate, pathname, openMenu, showWa }) {
  const items = [
    { key: "home", label: "Home", icon: HomeIcon, to: "/" },
    { key: "tasks", label: "Tasks", icon: CheckSquare, to: "/today" },
    showWa ? { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, to: WA_HOME, wa: true }
      : { key: "search", label: "Search", icon: Search, onClick: () => document.querySelector("[data-testid='nav-search-input']")?.focus() },
    { key: "alerts", label: "Alerts", icon: Bell, to: "/notifications" },
    { key: "more", label: "More", icon: LayoutGrid, onClick: openMenu },
  ];
  return (
    <nav aria-label="Mobile navigation" data-testid="os-mobile-bar"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 border-t border-[color:var(--st-border)] bg-[color:var(--st-surface)] flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]">
      {items.map((it) => {
        const Icon = it.icon; const cur = it.to && (pathname === it.to || (it.wa && pathname.startsWith(it.to)));
        return (
          <button key={it.key} onClick={() => (it.to ? navigate(it.to) : it.onClick?.())} data-testid={`os-mobile-${it.key}`}
            className={"flex-1 flex flex-col items-center justify-center gap-1 text-[10px] font-medium " + (cur ? (it.wa ? "text-emerald-700" : "text-[color:var(--st-accent)]") : it.wa ? "text-emerald-700/80" : "text-[color:var(--st-text-muted)]")}>
            <Icon className="h-5 w-5" strokeWidth={cur ? 2.2 : 1.7} />{it.label}
          </button>
        );
      })}
    </nav>
  );
}

function ProfileMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const go = (to) => { setOpen(false); navigate(to); };
  const switchShell = (v) => { try { window.localStorage.setItem("st.shell", v); } catch { /* ignore */ } window.location.reload(); };
  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button className="h-9 w-9 rounded-full bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] grid place-items-center text-[12px] font-bold ring-1 ring-[color:var(--st-border)]"
          aria-expanded={open} data-testid={NAV.profileMenu}>
          {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs">
          <div className="text-[color:var(--st-text)] font-medium">{user?.name || user?.email}</div>
          <div className="text-[10px] text-[color:var(--st-text-muted)] capitalize">{(user?.role || "").replace(/_/g, " ")}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => go("/my-devices")}><Laptop2 className="h-4 w-4 mr-2" /> My devices</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go("/settings/experience")} data-testid="profile-experience"><Sun className="h-4 w-4 mr-2" /> Experience</DropdownMenuItem>
        {canAdminUsers(user?.role) && <DropdownMenuItem onSelect={() => go("/users")}><UserCog className="h-4 w-4 mr-2" /> Users & Roles</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => switchShell("spaces")} data-testid="switch-to-spaces"><LayoutGrid className="h-4 w-4 mr-2" /> Switch to ONE Spaces</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => switchShell("classic")} data-testid="switch-to-classic"><LayoutGrid className="h-4 w-4 mr-2" /> Switch to Classic</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={AUTH.logoutBtn} onSelect={() => { setOpen(false); onLogout(); }} className="text-rose-600 focus:text-rose-700">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
